

import { useState, useEffect, useMemo } from "react";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import RoomForm from "./RoomForm";
import { mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrency } from "@/lib/data";
import { DoorOpen, Edit2, Trash2, Plus, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Download, CheckSquare, Square, Power, Search } from "lucide-react";
import { RoomFilters } from "./components";

const typeIcons: Record<string, string> = {
  "Banquet": "🏛️",
  "Open Air": "🌿",
  "Conference": "💼",
  "Accommodation": "🛏️"
};

const typeColors: Record<string, string> = {
  "Banquet": "bg-amber-50 text-amber-700",
  "Open Air": "bg-emerald-50 text-emerald-700",
  "Conference": "bg-indigo-50 text-indigo-700",
  "Accommodation": "bg-rose-50 text-rose-700"
};

interface Room {
  id: number;
  name: string;
  type: string;
  capacity: number;
  rate: number;
  status: string;
  description?: string;
  is_active?: boolean;
}

export default function RoomsPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  
  // Filter and sort state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortField, setSortField] = useState<'name' | 'capacity' | 'rate' | 'status' | 'type'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Bulk actions state
  const [selectedRooms, setSelectedRooms] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch rooms from API
  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const response = await mastersApi.rooms.list();
      const roomsData = response.data?.data || response.data || [];
      setRooms(roomsData);
    } catch (error) {
      showToast("Failed to load rooms", "error");
      console.error("Error loading rooms:", error);
    } finally {
      setLoading(false);
    }
  };
  

  // Handle sort
  const handleSort = (field: 'name' | 'capacity' | 'rate' | 'status' | 'type') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'capacity' | 'rate' | 'status' | 'type') => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-stone-400" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-amber-600" /> : 
      <ArrowDown className="w-4 h-4 text-amber-600" />;
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this room?")) return;
    try {
      await mastersApi.rooms.delete(id);
      showToast("Room deleted successfully", "success");
      loadRooms();
    } catch (error) {
      showToast("Failed to delete room", "error");
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingRoom) {
        await mastersApi.rooms.update(editingRoom.id, data);
        showToast("Room updated successfully", "success");
      } else {
        await mastersApi.rooms.create(data);
        showToast("Room created successfully", "success");
      }
      setIsModalOpen(false);
      setEditingRoom(null);
      loadRooms();
    } catch (error) {
      showToast("Failed to save room", "error");
    }
  };

  // Filter and sort rooms
  const filteredAndSortedRooms = useMemo(() => {
    let filtered = rooms;
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(room => 
        room.name.toLowerCase().includes(searchLower) ||
        (room.description && room.description.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply status filter
    if (statusFilter === 'available') {
      filtered = filtered.filter(room => room.status === 'available');
    } else if (statusFilter === 'booked') {
      filtered = filtered.filter(room => room.status === 'booked');
    }
    
    // Apply type filter
    if (typeFilter) {
      filtered = filtered.filter(room => room.type === typeFilter);
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'capacity':
          comparison = a.capacity - b.capacity;
          break;
        case 'rate':
          comparison = a.rate - b.rate;
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [rooms, search, statusFilter, typeFilter, sortField, sortDirection]);

  // Pagination
  const paginatedRooms = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedRooms.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedRooms, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedRooms.length / itemsPerPage);
  
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRooms(new Set()); // Clear selection on filter change
  }, [search, statusFilter, typeFilter, sortField, sortDirection]);
  
  // Show bulk actions when items are selected
  useEffect(() => {
    setShowBulkActions(selectedRooms.size > 0);
  }, [selectedRooms]);
  
  // Handle bulk selection
  const toggleRoomSelection = (id: number) => {
    setSelectedRooms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const toggleAllRooms = () => {
    if (selectedRooms.size === paginatedRooms.length) {
      setSelectedRooms(new Set());
    } else {
      setSelectedRooms(new Set(paginatedRooms.map(r => r.id)));
    }
  };
  
  // Handle bulk toggle status
  const handleBulkToggleStatus = async (newStatus: string) => {
    if (!confirm(`Set ${selectedRooms.size} rooms to ${newStatus}?`)) return;
    
    try {
      for (const id of selectedRooms) {
        const room = rooms.find(r => r.id === id);
        if (room) {
          await mastersApi.rooms.update(id, { ...room, status: newStatus });
        }
      }
      showToast(`${selectedRooms.size} rooms updated`, "success");
      setSelectedRooms(new Set());
      loadRooms();
    } catch (error) {
      showToast("Failed to update rooms", "error");
    }
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedRooms.size} rooms? This action cannot be undone.`)) return;
    
    try {
      for (const id of selectedRooms) {
        await mastersApi.rooms.delete(id);
      }
      showToast(`${selectedRooms.size} rooms deleted`, "success");
      setSelectedRooms(new Set());
      loadRooms();
    } catch (error) {
      showToast("Failed to delete rooms", "error");
    }
  };
  
  // Handle CSV export
  const handleExportCSV = () => {
    const roomsToExport = selectedRooms.size > 0 
      ? rooms.filter(r => selectedRooms.has(r.id))
      : filteredAndSortedRooms;
    
    const headers = ['Room Name', 'Type', 'Capacity', 'Rate', 'Status', 'Description'];
    const rows = roomsToExport.map(room => [
      room.name,
      room.type,
      room.capacity,
      room.rate,
      room.status,
      room.description || '',
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rooms-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    if (selectedRooms.size > 0) {
      setSelectedRooms(new Set());
    }
    showToast('CSV exported successfully', 'success');
  };

  if (loading) {
    return (
      <DashboardLayout>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <div className="h-6 w-32 bg-stone-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-48 bg-stone-200 rounded animate-pulse" />
          </div>
          <div className="h-9 w-32 bg-stone-200 rounded-xl animate-pulse" />
        </div>


        {/* Filters Skeleton */}
        <div className="h-16 bg-stone-100 rounded-xl border border-stone-200 mb-4 animate-pulse" />

        {/* Sort Controls Skeleton */}
        <div className="h-10 bg-stone-100 rounded-lg mb-4 animate-pulse" />

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="h-12 bg-stone-100 border-b border-stone-200 animate-pulse" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 border-b border-stone-100 animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-stone-800">Rooms</h1>
          <p className="text-sm text-stone-500">Venue rooms and accommodation</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 rounded-xl bg-stone-100 text-stone-700 font-semibold text-sm flex items-center gap-2 hover:bg-stone-200 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          {!isStaff && (
            <button 
              onClick={() => { setEditingRoom(null); setIsModalOpen(true); }}
              className="px-4 py-2 rounded-xl gold-gradient text-white font-semibold text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Room
            </button>
          )}
        </div>
      </div>


      {/* Filters */}
      <RoomFilters
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        onApply={() => {}} // Filters are applied reactively
      />

      {/* Bulk Actions Bar */}
      {showBulkActions && !isStaff && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-amber-800">{selectedRooms.size} rooms selected</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleBulkToggleStatus('available')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-sm font-medium hover:bg-emerald-200 transition-colors"
            >
              <DoorOpen className="w-4 h-4" />
              Set Available
            </button>
            <button
              onClick={() => handleBulkToggleStatus('booked')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-sm font-medium hover:bg-stone-200 transition-colors"
            >
              <Power className="w-4 h-4" />
              Set Booked
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-100 text-rose-700 text-sm font-medium hover:bg-rose-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
            <button
              onClick={() => setSelectedRooms(new Set())}
              className="px-3 py-2 rounded-lg bg-white text-stone-600 text-sm font-medium hover:bg-stone-100 transition-colors border border-stone-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Sort and View Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-stone-500">Sort by:</span>
          <button
            onClick={() => handleSort('name')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'name' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Name {getSortIcon('name')}
          </button>
          <button
            onClick={() => handleSort('type')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'type' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Type {getSortIcon('type')}
          </button>
          <button
            onClick={() => handleSort('capacity')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'capacity' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Capacity {getSortIcon('capacity')}
          </button>
          <button
            onClick={() => handleSort('rate')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'rate' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Rate {getSortIcon('rate')}
          </button>
          <button
            onClick={() => handleSort('status')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'status' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Status {getSortIcon('status')}
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Per Page Selector */}
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="text-xs border border-stone-300 rounded px-2 py-1.5 bg-white"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      {/* Rooms Table */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {!isStaff && (
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider w-10">
                  <button
                    onClick={toggleAllRooms}
                    className="hover:text-amber-600 transition-colors"
                  >
                    {selectedRooms.size === paginatedRooms.length && paginatedRooms.length > 0 ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
              )}
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Room</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Capacity</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Rate</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {paginatedRooms.map((room) => {
              const isSelected = selectedRooms.has(room.id);
              return (
                <tr key={room.id} className={`hover:bg-stone-50 transition-colors ${isSelected ? 'bg-amber-50' : ''}`}>
                  {!isStaff && (
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleRoomSelection(room.id)}
                        className="hover:text-amber-600 transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-amber-600" />
                        ) : (
                          <Square className="w-4 h-4 text-stone-400" />
                        )}
                      </button>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-stone-800">{room.name}</p>
                      {room.description && (
                        <p className="text-xs text-stone-500 line-clamp-1">{room.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{typeIcons[room.type] || '🏠'}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeColors[room.type]}`}>{room.type}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-stone-600">{room.capacity} guests</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-stone-700">{formatCurrency(room.rate)}</p>
                    <p className="text-xs text-stone-400">/day</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge label={room.status} status={room.status} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!isStaff && (
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => { setEditingRoom(room); setIsModalOpen(true); }}
                          className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(room.id)}
                          className="p-2 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {paginatedRooms.length === 0 && (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <DoorOpen className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">
            {search || statusFilter || typeFilter ? 'No rooms match your filters' : 'No rooms yet'}
          </p>
          {!isStaff && !search && !statusFilter && !typeFilter && (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="mt-3 px-4 py-2 rounded-xl gold-gradient text-white text-sm font-medium"
            >
              Add First Room
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-stone-500">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedRooms.length)} of {filteredAndSortedRooms.length} rooms
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum 
                        ? 'bg-amber-500 text-white' 
                        : 'border border-stone-300 text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Room Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingRoom(null); }}
        title={editingRoom ? "Edit Room" : "Add New Room"}
        size="md"
      >
        <RoomForm
          initialData={editingRoom ? {
            name: editingRoom.name,
            type: editingRoom.type,
            capacity: editingRoom.capacity.toString(),
            rate: editingRoom.rate.toString(),
            status: editingRoom.status,
          } : undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingRoom(null); }}
        />
      </Modal>
    </DashboardLayout>
  );
}
